import React, { ReactNode } from 'react'
import { CheckPicker, SelectPicker, Tag } from 'rsuite'
import FilterField, { FilterFieldSize } from './FilterField'

interface BaseSelectProps<T> {
  data: { label: string; value: T; color?: string }[]
  placeholder?: string
  label?: string
  fieldSize?: FilterFieldSize
  searchable?: boolean
  loading?: boolean
  onSearch?: (keyword: string) => void
  onOpen?: () => void
  onClose?: () => void
  renderValue?: (value: T | T[], items: any, selectedElement: ReactNode) => ReactNode
  cleanable?: boolean
}

export interface FilterSelectProps<T = string> extends BaseSelectProps<T> {
  value: T | null
  onChange: (value: T | null) => void
}

export interface FilterCheckPickerProps<T = string> extends BaseSelectProps<T> {
  value: T[]
  onChange: (value: T[]) => void
  showColoredItems?: boolean
  renderMenuItem?: (label: ReactNode, item: any) => ReactNode
}

/** Одиночный выбор */
export function FilterSelect<T = string> ({
  value,
  onChange,
  data,
  placeholder = 'Выберите',
  label,
  fieldSize = 'normal',
  searchable = false,
  loading,
  onSearch,
  onOpen,
  onClose,
  cleanable = true
}: FilterSelectProps<T>) {
  return (
    <FilterField size={fieldSize} label={label}>
      <SelectPicker
        value={value}
        onChange={onChange}
        data={data}
        placeholder={placeholder}
        size="sm"
        searchable={searchable}
        loading={loading}
        onSearch={onSearch}
        onOpen={onOpen}
        onClose={onClose}
        cleanable={cleanable}
        block
      />
    </FilterField>
  )
}

/** Множественный выбор */
export function FilterCheckPicker<T = string> ({
  value,
  onChange,
  data,
  placeholder = 'Выберите',
  label,
  fieldSize = 'normal',
  searchable = false,
  loading,
  onSearch,
  onOpen,
  onClose,
  showColoredItems = false,
  cleanable = true,
  renderMenuItem: customRenderMenuItem
}: FilterCheckPickerProps<T>) {
  // Определяем renderMenuItem
  const renderMenuItem = customRenderMenuItem ?? (showColoredItems ? (itemLabel: ReactNode, item: any) => (
    <Tag key={item?.value} color={item?.color}>
      {itemLabel}
    </Tag>
  ) : undefined)

  return (
    <FilterField size={fieldSize} label={label}>
      <CheckPicker
        value={value}
        onChange={onChange}
        data={data}
        placeholder={placeholder}
        size="sm"
        searchable={searchable}
        loading={loading}
        onSearch={onSearch}
        onOpen={onOpen}
        onClose={onClose}
        cleanable={cleanable}
        block
        renderMenuItem={renderMenuItem}
      />
    </FilterField>
  )
}
